import { useEffect, useMemo, useState } from "react"
import { useRoutesHooks } from "../../../../../features/routes"
import { DetailWrapper, SpinnerDiv } from "../../../../../features/ui"
import { Customer, HoardOrder } from "../../../state/types"
import { AdjustmentNote, Check, CreditPayment, useAdjustmentNoteLoaders, useCheckLoaders, useCreditPaymentLoaders } from "../../../../administration"
import { HoardDetail } from "./hoardDetail"
import { useRoadmapLoaders } from "../../../../logistics"
import { useCustomerLoaders } from "../../../loaders/customerLoaders"
import { useHoardLoaders } from "../../../loaders/hoardLoaders"
import { defined } from "../../../../../utils/listUtils"
import { FiscalDocument, FiscalDocumentStatus, useFiscalDocumentAutoRefresh, useFiscalDocumentLoaders } from "../../../../fiscal-documents"
import { Delivery, DeliveryOrder, useDeliveryLoaders, useDeliveryOrderLoaders } from "../../../../deliveries"
import { useDeliveriesState } from "../../../../deliveries/hooks/deliveriesHooks"
import { useServer } from "../../../../../services/server"

export const Loader = () => {
    const { dataType } = useRoutesHooks().parseUrl()
    const hoard = dataType! as HoardOrder

    const { deliveries: stateDeliveries } = useDeliveriesState()

    const [ready, setReady] = useState(false)
    const [deliveryOrders, setDeliveryOrders] = useState<DeliveryOrder[]>([])
    const [deliveries, setDeliveries] = useState<Delivery[]>([])
    const [checks, setChecks] = useState<Check[]>([])
    const [customer, setCustomer] = useState<Customer>()
    const [creditPayments, setCreditPayments] = useState<CreditPayment[]>([])
    const [fiscalDocuments, setFiscalDocuments] = useState<FiscalDocument[]>([])
    const [adjustmentNotes, setAdjustmentNotes] = useState<AdjustmentNote[]>([])
    const [itemsLength, setItemsLength] = useState(0)

    const deliveryOrderLoaders = useDeliveryOrderLoaders()
    const deliveryLoaders = useDeliveryLoaders()
    const checkLoaders = useCheckLoaders()
    const roadmapLoaders = useRoadmapLoaders()
    const customerLoaders = useCustomerLoaders()
    const creditPaymentLoaders = useCreditPaymentLoaders()
    const fiscalDocumentLoaders = useFiscalDocumentLoaders()
    const adjustmentNoteLoaders = useAdjustmentNoteLoaders()
    const hoardLoaders = useHoardLoaders()

    const { refreshFiscalDocumentStatus } = useServer()

    const loadDeliveries = async (optimized = true) => {
        const { loadOperationDeliveryOrders } = deliveryOrderLoaders()
        const loadedDeliveryOrders = await loadOperationDeliveryOrders(hoard.id, optimized)
        setDeliveryOrders(loadedDeliveryOrders)
        const { loadOperationDeliveries } = deliveryLoaders()
        const loadedDeliveries = await loadOperationDeliveries(hoard.id, optimized)
        setDeliveries(loadedDeliveries)
        return loadedDeliveries
    }

    // El estado de facturación también vive denormalizado en la operación (invoiceStatus). Al refrescar
    // el documento hay que releer el acopio para que la UI (botón Facturar, editabilidad, lista) no quede vieja.
    const reloadFiscalDocuments = async () => {
        const { loadHoard } = hoardLoaders()
        await loadHoard(hoard.id, true)
        const { loadOperationFiscalDocuments } = fiscalDocumentLoaders()
        setFiscalDocuments(await loadOperationFiscalDocuments(hoard.id, false))
        const { loadOperationAdjustmentNotes } = adjustmentNoteLoaders()
        setAdjustmentNotes(await loadOperationAdjustmentNotes(hoard.id, false))
    }

    // Annulling or cancelling pending items can also generate a credit note
    const reloadDeliveries = async () => {
        await loadDeliveries(false)
        await reloadFiscalDocuments()
    }

    // Autorefresco: mientras haya comprobantes pendientes de autorizar, consultamos Facturante y
    // releemos. Se detiene solo al llegar a estado terminal o al agotar la ventana (~30s), momento
    // en que la UI vuelve a ofrecer el refresh manual.
    const pendingFiscalDocumentIds = useMemo(
        () => fiscalDocuments
            .filter(document => document.status === FiscalDocumentStatus.TO_AUTHORIZE)
            .map(document => document.id),
        [fiscalDocuments]
    )

    const refreshPendingFiscalDocuments = async (pendingIds: string[]) => {
        await Promise.all(pendingIds.map(id => refreshFiscalDocumentStatus(id)))
        await reloadFiscalDocuments()
    }

    const { isExhausted: isAutoRefreshExhausted } = useFiscalDocumentAutoRefresh(
        pendingFiscalDocumentIds,
        refreshPendingFiscalDocuments
    )

    const load = async () => {
        const loadedDeliveries = await loadDeliveries()
        const { loadChecks } = checkLoaders()
        const destinationIds = defined(hoard.paymentItems.map(paymentItem => paymentItem.destinationId))
        const checks = await loadChecks(destinationIds)
        setChecks(checks)
        const { loadDeliveriesRoadmapsLinks } = roadmapLoaders()
        await loadDeliveriesRoadmapsLinks(loadedDeliveries.map(delivery => delivery.id))
        const { loadCustomer } = customerLoaders()
        const loadedCustomer = await loadCustomer(hoard.customerId)
        setCustomer(loadedCustomer)
        const { loadCustomerCreditPayments } = creditPaymentLoaders()
        const loadedCreditPayments = await loadCustomerCreditPayments(hoard.customerId)
        const operationCreditPayments = loadedCreditPayments.filter(creditPayment => creditPayment.operationItems.some(operationItem => operationItem.operationId === hoard.id))
        setCreditPayments(operationCreditPayments)
        const { loadOperationFiscalDocuments } = fiscalDocumentLoaders()
        const loadedFiscalDocuments = await loadOperationFiscalDocuments(hoard.id)
        setFiscalDocuments(loadedFiscalDocuments)
        const { loadOperationAdjustmentNotes } = adjustmentNoteLoaders()
        const loadedAdjustmentNotes = await loadOperationAdjustmentNotes(hoard.id)
        setAdjustmentNotes(loadedAdjustmentNotes)
    }
    
    useEffect(() => {
        if (itemsLength === 0 || stateDeliveries.length < itemsLength) {
            setItemsLength(stateDeliveries.length)
            setReady(false)
            load().then(() => setReady(true))
        }
    }, [itemsLength, stateDeliveries.length])

    return ready ? (
        <HoardDetail
            hoard={hoard}
            deliveryOrders={deliveryOrders}
            deliveries={deliveries}
            checks={checks}
            customer={customer}
            creditPayments={creditPayments}
            fiscalDocuments={fiscalDocuments}
            adjustmentNotes={adjustmentNotes}
            onRefreshDeliveries={reloadDeliveries}
            onRefreshFiscalDocuments={reloadFiscalDocuments}
            isAutoRefreshExhausted={isAutoRefreshExhausted}
        />
    ) : <SpinnerDiv />
}

export const HoardDetailLoader = () => (
    <DetailWrapper>
        <Loader />
    </DetailWrapper>
)
